import { Controller, useFormContext } from "react-hook-form";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { Membership } from "@/types/system-setting/membership";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetIncomeCategorListQuery, useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetSalesTaxListQuery, useGetTaxGroupQuery } from "@/services/salesTaxApi";
import { RootState } from "@/redux/store";
import { Card } from "@/components/ui/card";
import Combobox from "@/components/ui/common/combobox";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

const PriceDiscountTaxForm = () => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const { data: incomeCategoryData } = useGetIncomeCategoryQuery({
    org_id: orgId, query: "status=active"
  });

  const { data: taxGroupData } = useGetTaxGroupQuery({
    query: "",
    org_id: orgId,
  });

  const taxGroupOptions = useMemo(() => {
    return Array.isArray(taxGroupData?.data)
      ? taxGroupData?.data.map((taxGroup) => ({
        label: taxGroup.name,
        value: String(taxGroup.id),
      }))
      : [];
  }, [taxGroupData]);

  const incomeCategoryOptions = useMemo(() => {
    return Array.isArray(incomeCategoryData?.data)
      ? incomeCategoryData?.data.map((incomeCategory) => ({
        label: incomeCategory.name,
        value: String(incomeCategory.id),
      }))
      : [];
  }, [incomeCategoryData]);

  const {
    control,
    formState: { errors },
    register,
    setValue,
    getValues,
    trigger,
    watch,
  } = useFormContext<Membership>();


  const [autoRenewal, setAutoRenewal] = useState<boolean | undefined>(
    (getValues("auto_renewal") as boolean) || false
  );

  useEffect(() => {
    const subscription = watch((value) => {
      setAutoRenewal(value?.auto_renewal);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const handleDayInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: keyof Membership
  ) => {
    const value = parseInt(e.target.value, 10);
    if (value >= 15) {
      setValue(field, 15);
    }
  };



  return (
    <div className="text-black space-y-4">
      <Card className="p-4">
        <h1 className="font-semibold text-[#2D374] text-xl">
          Price, Discounts, and Tax Details
        </h1>
        <div className="grid grid-cols-3 gap-4 items-start mt-3">
          <FloatingLabelInput
            id="net_price"
            label="Net Price"
            text="*"
            type="number"
            min={0}
            step={1}
            onKeyDown={(e) => {
              if (e.key === '.') {
                e.preventDefault();
              }
            }}
            {...register("net_price", {
              required: "Required",
              min: {
                value: 1,
                message: "Price must be more than 1",
              },
              pattern: {
                value: /^[0-9]+$/,
                message: "Only whole numbers are allowed"
              }
            })}
            error={errors.net_price?.message}
          />
          <FloatingLabelInput
            id="reg_fee"
            label="Registration Fee"
            type="number"
            min={0}
            {...register("reg_fee", {
              setValueAs: (value) => (value === "" ? undefined : Number(value)), // Converts string to number
            })}
            error={errors.reg_fee?.message}
          />
          <FloatingLabelInput
            id="discount"
            label="Discount Percentage"
            type="number"
            min={0}
            max={99}
            {...register("discount", {
              // required: "Required",
              valueAsNumber: true,
              min: 0,
              max: {
                value: 99,
                message: "Discount must be between 0 and 99.",
              },
            })}
            error={errors.discount?.message}
          />
          <Controller
            name="income_category_id"
            rules={{ required: "Required" }}
            control={control}
            render={({
              field: { onChange, value, onBlur },
              fieldState: { invalid, error },
            }) => (
              <div>
                <Combobox
                  list={incomeCategoryOptions}
                  defaultValue={incomeCategoryOptions.find(option => option.value === value?.toString())?.label || ""}
                  setFilter={(selectedLabel: string) => {
                    const selectedOption = incomeCategoryOptions.find(option => option.label === selectedLabel);
                    onChange(selectedOption ? Number(selectedOption.value) : undefined);
                  }}
                  label="Income Category"
                />
                {error && (
                  <span className="text-red-500 text-xs mt-[5px]">
                    {error.message}
                  </span>
                )}
              </div>
            )}
          />
          
          <Controller
            name="tax_group_id"
            rules={{ required: "Required" }}
            control={control}
            render={({
              field: { onChange, value, onBlur },
              fieldState: { invalid, error },
            }) => (
              <div>
                <Combobox
                  list={taxGroupOptions}
                  defaultValue={taxGroupOptions.find(option => option.value === value?.toString())?.label || ""}
                  setFilter={(selectedLabel: string) => {
                    const selectedOption = taxGroupOptions.find(option => option.label === selectedLabel);
                    onChange(selectedOption ? Number(selectedOption.value) : undefined);
                  }}
                  label="Tax Group"
                />
                {error && (
                  <span className="text-red-500 text-xs mt-[5px]">
                    {error.message}
                  </span>
                )}
              </div>
            )}
          />

        </div>
      </Card>



    </div>
  );
};

export default PriceDiscountTaxForm;